import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from '../../../i18n/i18n';
import { toDate } from '../../../utils/timeUtils';
import { AuthContext } from '../../../context/AuthContext';
import { useRecipeDisplay } from '../../../context/RecipeDisplayContext';
import { EVENT_NAMES } from '../../../utils/eventLogging/eventNames';
import MaterialCorrectionList from './MaterialCorrectionList';
import ModalShell from '../../../components/ui/ModalShell';
import { Loader2 } from 'lucide-react';
import useOnlineStatus from '../../../hooks/useOnlineStatus';

function getOpenedKey(userName, correctionId) {
  return `${userName || 'unknown'}|${correctionId}`;
}

/**
 * Correction details + acknowledgement modal:
 * - shows unconfirmed corrections from productionEvents for the active operator,
 * - marks corrections as confirmed when operator presses the Confirm button.
 *
 * Shared correction state (active operator, events, normalization, confirm action)
 * is sourced from RecipeDisplayContext; this component only handles selection and UI.
 */
export default function ProductionCorrectionsModal({ forcedCorrection, disableAutoPick = false, onClose }) {
  const {
    currentUser,
    productionEvents = [],
    activeUserName,
    formatCorrectionDate,
    markCorrectionConfirmed,
    normalizeCorrection,
  } = useRecipeDisplay();
  const { logEventCustom } = useContext(AuthContext);
  const { t } = useTranslation(currentUser?.info?.languageCode);
  const isOnline = useOnlineStatus();
  const [activeCorrection, setActiveCorrection] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState(false);
  // Prevent repeated opens for the same correction/operator during this mount.
  const openedCorrectionKeysRef = useRef(new Set());

  // All unexecuted corrections from the current production (material + time).
  // productionEvents is already scoped to the current production_id by the subscription.
  const actionableCorrections = useMemo(
    () =>
      productionEvents.filter(
        (e) => (e?.type === 'material_correction' || e?.type === 'time_correction') && e?.executed !== true,
      ),
    [productionEvents],
  );

  // Normalize correction materials (ingredient metadata + numeric deltas) for display.
  const displayCorrection = useMemo(
    () => normalizeCorrection(activeCorrection?.correction),
    [activeCorrection?.correction, normalizeCorrection],
  );

  useEffect(() => {
    // Operator switch resets the currently shown correction; auto-pick will re-evaluate.
    setActiveCorrection(null);
  }, [activeUserName]);

  useEffect(() => {
    if (!forcedCorrection) return;

    // Forced correction path: opened from production details or TopNav button.
    // Always allowed (online or offline) — offline just renders a Close button instead of Confirm.
    openedCorrectionKeysRef.current.add(getOpenedKey(activeUserName, forcedCorrection.id));
    setActiveCorrection({ correction: forcedCorrection, correctionId: forcedCorrection.id });
  }, [forcedCorrection, activeUserName]);

  useEffect(() => {
    if (forcedCorrection) return;
    if (disableAutoPick) return;
    // Confirmation requires runTransaction, which doesn't work offline — don't auto-open while offline.
    if (!isOnline) return;
    if (!actionableCorrections.length || activeCorrection) return;

    // Auto-pick first unconfirmed correction not yet shown to this operator.
    const nextUnconfirmed = actionableCorrections.find((correction) => {
      const confirmedBy = Array.isArray(correction?.confirmed_by) ? correction.confirmed_by : [];
      if (confirmedBy.includes(activeUserName)) return false;
      return !openedCorrectionKeysRef.current.has(getOpenedKey(activeUserName, correction.id));
    });

    if (!nextUnconfirmed) return;

    openedCorrectionKeysRef.current.add(getOpenedKey(activeUserName, nextUnconfirmed.id));
    setActiveCorrection({ correction: nextUnconfirmed, correctionId: nextUnconfirmed.id });
  }, [activeCorrection, activeUserName, actionableCorrections, forcedCorrection, disableAutoPick, isOnline]);

  useEffect(() => {
    if (!activeCorrection?.correctionId || !actionableCorrections.length) return;

    // Keep modal content in sync with real-time updates from productionEvents.
    const updatedCorrection = actionableCorrections.find((e) => e.id === activeCorrection.correctionId);

    if (!updatedCorrection) return;

    setActiveCorrection((prev) => {
      if (!prev) return prev;
      return { ...prev, correction: updatedCorrection };
    });
  }, [activeCorrection?.correctionId, actionableCorrections]);

  function handleCloseModal() {
    setActiveCorrection(null);
    if (onClose) onClose();
  }

  function handleOfflineClose() {
    // Allow the user to dismiss the modal while offline. The id is removed from
    // the "already opened" ref so auto-pick can re-open it once the app is online.
    // onClose is invoked so the parent resets its forced-correction state — this
    // lets the user re-open the modal manually while still offline (otherwise the
    // parent state would stay "open" and the next click would be a no-op).
    if (activeCorrection?.correctionId) {
      openedCorrectionKeysRef.current.delete(getOpenedKey(activeUserName, activeCorrection.correctionId));
    }
    setActiveCorrection(null);
    if (onClose) onClose();
  }

  async function handleConfirm() {
    flushSync(() => {
      setIsConfirming(true);
      setConfirmError(false);
    });

    try {
      await markCorrectionConfirmed(activeCorrection.correction.id, activeCorrection.correction);

      // Only once the confirmation is durably written, so a failed confirm is never counted.
      logEventCustom(EVENT_NAMES.CORRECTION_CONFIRM, {
        production_id: activeCorrection.correction.production_id || null,
        correction_id: activeCorrection.correction.id || null,
        // A time correction carries no materials, so the type is what makes a count of 0 readable.
        correction_type: activeCorrection.correction.type || null,
        num_materials: Array.isArray(activeCorrection.correction.materials)
          ? activeCorrection.correction.materials.length
          : 0,
      });

      setIsConfirming(false);
      handleCloseModal();
    } catch {
      setIsConfirming(false);
      setConfirmError(true);
    }
  }

  if (!activeCorrection) return null;

  const isMaterialCorrection = activeCorrection.correction.type === 'material_correction';
  const isTimeCorrection = activeCorrection.correction.type === 'time_correction';
  const isAlreadyConfirmed =
    Array.isArray(activeCorrection.correction.confirmed_by) &&
    activeCorrection.correction.confirmed_by.includes(activeUserName);
  const previousStartDate = toDate(activeCorrection.correction.previous_start_date);
  const newStartDate = toDate(activeCorrection.correction.new_start_date);
  const previousEndDate = toDate(activeCorrection.correction.previous_end_date);
  const newEndDate = toDate(activeCorrection.correction.new_end_date);
  const startChanged =
    previousStartDate && newStartDate ? previousStartDate.getTime() !== newStartDate.getTime() : false;
  const endChanged = previousEndDate && newEndDate ? previousEndDate.getTime() !== newEndDate.getTime() : false;

  return (
    <ModalShell
      title={t('production_corrections_details_title')}
      onClose={isAlreadyConfirmed ? handleCloseModal : !isOnline ? handleOfflineClose : undefined}
      showClose={(isAlreadyConfirmed || !isOnline) && !isConfirming}
      closeOnBackdrop={(isAlreadyConfirmed || !isOnline) && !isConfirming}
      maxWidth={isMaterialCorrection ? '40.625rem' : '34.375rem'}
    >
      <div className='flex gap-4'>
        <div className='flex flex-col gap-1 flex-1'>
          <span className='text-sm leading-normal font-medium text-[#8A8C95] dark:text-[#CAD5E2]'>
            {t('production_corrections_created')}
          </span>
          <div className='text-base leading-[140%] font-medium text-[#2D3041] dark:text-[#FAFAFA]'>
            {formatCorrectionDate(activeCorrection.correction.created_at) || t('common_not_available')}
          </div>
        </div>
        <div className='flex flex-col gap-1 flex-1'>
          <span className='text-sm leading-normal font-medium text-[#8A8C95] dark:text-[#CAD5E2]'>
            {t('production_corrections_manager')}
          </span>
          <div className='text-base leading-[140%] font-medium text-[#2D3041] dark:text-[#FAFAFA]'>
            {activeCorrection.correction.user_name || t('common_unknown')}
          </div>
        </div>
      </div>

      {isMaterialCorrection && (
        <MaterialCorrectionList
          materials={displayCorrection?.materials}
          deviceGroups={displayCorrection?.deviceGroups}
          deviceNames={currentUser?.devices}
          languageCode={currentUser?.info?.languageCode}
        />
      )}

      {isTimeCorrection && (
        <div className='p-4 rounded-lg bg-[#F3F7FF] border border-[#DDE6F9] dark:border-none dark:bg-[#2E3440] flex flex-col gap-4'>
          <div className='text-base leading-[140%] font-medium text-[#16192C] dark:text-[#FAFAFA] pb-2.5 border-b border-b-[#DDE6F9] dark:border-b-[#3C3E53]'>
            {t('production_corrections_time_change')}
          </div>

          {!startChanged && !endChanged ? (
            <div className='text-sm text-[#8A8C95] dark:text-[#CAD5E2]'>
              {t('production_corrections_no_time_changes')}
            </div>
          ) : (
            <div className='p-3 rounded-lg bg-[#FFFFFF] dark:bg-[#242933] flex flex-col gap-3'>
              <span className='text-sm leading-[140%] font-medium text-[#1650C2] dark:text-[#3D8DD4]'>
                {t('production_corrections_new_schedule')}
              </span>
              <div>
                {startChanged && (
                  <div className='flex items-center gap-2'>
                    <span className='text-sm leading-normal font-medium text-[#8A8C95] dark:text-[#90A1B9] uppercase'>
                      {t('production_corrections_start')}:
                    </span>
                    <span className='text-sm leading-normal font-medium text-[#16192C] dark:text-[#FAFAFA]'>
                      {formatCorrectionDate(activeCorrection.correction.new_start_date) || t('common_not_available')}
                    </span>
                  </div>
                )}
                {endChanged && (
                  <div className='flex items-center gap-2'>
                    <span className='text-sm leading-normal font-medium text-[#8A8C95] dark:text-[#90A1B9] uppercase'>
                      {t('production_corrections_end')}:
                    </span>
                    <span className='text-sm leading-normal font-medium text-[#16192C] dark:text-[#FAFAFA]'>
                      {formatCorrectionDate(activeCorrection.correction.new_end_date) || t('common_not_available')}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {activeCorrection.correction.reasoning && (
        <div className='rounded-lg p-4 bg-[#F6F6F7] dark:bg-[#0F172A]'>
          <div className='text-sm leading-normal font-medium text-[#8A8C95] dark:text-[#CAD5E2]'>
            {t('production_corrections_reason')}
          </div>
          <div className='text-base leading-[140%] font-medium text-[#2D3041] dark:text-[#FAFAFA]'>
            {activeCorrection.correction.reasoning}
          </div>
        </div>
      )}
      <div className='w-full flex flex-col items-end gap-2'>
        {confirmError && <p className='text-sm text-[#E04B4B]'>{t('production_corrections_confirm_failed')}</p>}
        <div className='flex gap-3'>
          {!isOnline && !isAlreadyConfirmed && (
            <button
              type='button'
              className='rounded-md px-3 py-1.5 text-base font-medium bg-light-button dark:bg-dark-button text-light-button-text dark:text-dark-button-text border border-light-button-border dark:border-dark-button-border'
              onClick={handleOfflineClose}
            >
              {t('common_close')}
            </button>
          )}
          {isAlreadyConfirmed && (
            <button
              type='button'
              className='rounded-md px-3 py-1.5 text-base font-medium bg-light-button dark:bg-dark-button text-light-button-text dark:text-dark-button-text border border-light-button-border dark:border-dark-button-border'
              onClick={handleCloseModal}
            >
              {t('common_close')}
            </button>
          )}
          {isOnline && !isAlreadyConfirmed && (
            <button
              type='button'
              disabled={isConfirming}
              aria-busy={isConfirming}
              className='flex items-center justify-center gap-2 min-w-[6.5rem] text-lg leading-[120%] font-medium text-[#FFFFFF] dark:text-[#FAFAFA] rounded-lg bg-[#2563EB]  hover:bg-[#1E4FBB] active:bg-[#113C91] dark:bg-[#2773B7] dark:hover:bg-[#1D649C] dark:active:bg-[#2773B7] disabled:opacity-70 disabled:cursor-not-allowed py-2 px-4 h-11 box-border transition-all duration-300'
              onClick={handleConfirm}
            >
              {isConfirming ? <Loader2 className='size-5 animate-spin' /> : t('common_confirm')}
            </button>
          )}
        </div>
      </div>
    </ModalShell>
  );
}
